import type { AuthRequest, AuthState, BaseClientInfo } from '@/features/mcp/types';
import { servicesApi } from '@/services/api';
import { isAPIError } from '@/services/api/ts-rest';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { REQUIRED_PARAMS, parseAuthRequest } from './utils';
import { isEmptyString } from '@/utils/string';
import {
  Banner,
  Box,
  Button,
  Card,
  CardSection,
  Heading,
  Icon,
  Image,
  InlineStack,
  Link,
  PanelActions,
  PanelFooter,
  PanelSection,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { trustCard } from './index.css';
import { isEmpty, snakeCase } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorView } from './error';
import { AuthLayout } from './layout';
import { KNOWN_SCOPES, SelectScopes } from './select-scopes';
import { SelectWorkspace } from './select-workspace';

function parseScopeString(raw: string | undefined): string[] {
  if (raw == null) return [];
  return raw
    .split(/\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

interface BuildConsentBodyArgs {
  requestPayload: Record<string, string[]>;
  authRequestScope: string;
  signedAuthRequest: string;
  selectedScopes: string[];
  selectedWorkspaces: Record<string, boolean>;
}

function buildConsentBody({
  requestPayload,
  authRequestScope,
  signedAuthRequest,
  selectedScopes,
  selectedWorkspaces,
}: BuildConsentBodyArgs): string {
  const consentOwnsScope = authRequestScope.trim().length > 0;
  const body = new URLSearchParams();

  for (const [key, values] of Object.entries(requestPayload)) {
    if (consentOwnsScope && key === 'scope') continue;
    if (!Array.isArray(values)) continue;
    for (const value of values) {
      if (typeof value === 'string') body.append(key, value);
    }
  }

  if (consentOwnsScope) {
    // Sort for a stable wire value across toggle history.
    body.append('scope', [...selectedScopes].sort().join(' '));
  }

  // Signed blob: server reads the original requested_scope from inside.
  body.append('auth_request', signedAuthRequest);

  for (const [key, value] of Object.entries(selectedWorkspaces)) {
    if (value) body.append('workspace_ids', key);
  }

  return body.toString();
}

function extractApiErrorMessage(error: unknown): string {
  const fallback = 'Failed to connect to the authorization server. Please try again.';
  if (!isAPIError(error)) return fallback;
  const errorBody = error.response.body as { errors?: Array<{ detail?: string }> };
  return errorBody.errors?.[0]?.detail ?? fallback;
}

type McpOauth2Props = { canEditAccount: boolean };

export function McpOauth2({ canEditAccount }: McpOauth2Props) {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<AuthState>({
    isRedirecting: false,
  });
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Record<string, boolean>>({});
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const isMcpEnabled = useFeatureFlag(AccountFeatureFlag.McpExternalAccountToggle);

  // Memoised so SelectScopes' downstream useMemos don't churn per toggle.
  const requestedScopes = useMemo(() => parseScopeString(state.authRequest?.scope), [state.authRequest?.scope]);

  useEffect(() => {
    const authRequestParameter = searchParams.get('auth_request');
    if (isEmptyString(authRequestParameter)) {
      setState((previous) => ({
        ...previous,
        error: { type: 'missing_param', param: 'auth_request' },
      }));
      return;
    }

    try {
      const { request, payload, clientInfo } = parseAuthRequest(authRequestParameter);
      const missingParameter = REQUIRED_PARAMS.find((parameter) => {
        const value = request[parameter as keyof AuthRequest];
        return typeof value !== 'string' || isEmptyString(value);
      });
      if (missingParameter != null) {
        setState((previous) => ({
          ...previous,
          error: { type: 'missing_param', param: snakeCase(missingParameter) },
        }));
        return;
      }

      // Validate response type
      if (request.responseType !== 'code') {
        setState((previous) => ({
          ...previous,
          error: {
            type: 'invalid_param',
            param: 'response_type',
            message: 'Only authorization code flow is supported',
          },
        }));
        return;
      }

      // All-unknown scopes would lock the Allow button — show an error instead.
      const requested = parseScopeString(request.scope);
      if (requested.length > 0 && !requested.some((s) => KNOWN_SCOPES.has(s))) {
        setState((previous) => ({
          ...previous,
          error: {
            type: 'invalid_param',
            param: 'scope',
            message:
              'The requested permissions are not recognised by this consent screen. Please contact the app developer.',
          },
        }));
        return;
      }

      setState((previous) => ({
        ...previous,
        clientInfo,
        authRequest: request,
        requestPayload: payload,
        signedAuthRequest: authRequestParameter,
        error: undefined,
      }));
    } catch (_error) {
      setState((previous) => ({
        ...previous,
        error: {
          type: 'parse_error',
          message: 'Failed to decode or parse auth_request',
        },
      }));
    }
  }, [searchParams]);

  const handleAllowAccess = async () => {
    if (state.requestPayload == null) {
      setState((previous) => ({
        ...previous,
        error: { type: 'api_error', message: 'No request payload available. Please try again.' },
      }));
      return;
    }

    if (state.error != null) {
      setState((previous) => ({
        ...previous,
        error: { type: 'api_error', message: 'Cannot proceed with authorization due to existing errors.' },
      }));
      return;
    }

    const body = buildConsentBody({
      requestPayload: state.requestPayload,
      authRequestScope: state.authRequest?.scope ?? '',
      signedAuthRequest: state.signedAuthRequest ?? '',
      selectedScopes,
      selectedWorkspaces,
    });

    try {
      const { location } = await servicesApi.oauthMcp({ body });
      setState((previous) => ({ ...previous, isRedirecting: true }));
      window.location.href = location;
    } catch (error: unknown) {
      setState((previous) => ({
        ...previous,
        error: { type: 'api_error', message: extractApiErrorMessage(error) },
      }));
    }
  };

  const isAllowAccessDisabled = useMemo(() => {
    const noWorkspace =
      Object.keys(selectedWorkspaces).length === 0 ||
      Object.values(selectedWorkspaces).every((value) => value === false);
    // Gate on scope selection only when the client requested scopes.
    const requestedScope = state.authRequest?.scope ?? '';
    const noScope = requestedScope.trim().length > 0 && selectedScopes.length === 0;
    return noWorkspace || noScope;
  }, [selectedWorkspaces, selectedScopes, state.authRequest?.scope]);

  if (state.error != null) {
    return <ErrorView error={state.error} />;
  }

  if (state.isRedirecting) {
    return <RedirectView clientName={state.clientInfo?.clientName} />;
  }

  return (
    <AuthLayout>
      <PanelSection>
        {(() => {
          const logoUri = state.clientInfo?.logoUri;
          const clientName = state.clientInfo?.clientName ?? 'this app';

          return !isEmptyString(logoUri) ? (
            <Stack gap="100" alignBlock="center" alignInline="center" mb="300">
              <Image src={logoUri} alt={clientName} size="icon-lg" style={{ borderRadius: '20%' }} />
            </Stack>
          ) : null;
        })()}

        {!isMcpEnabled ? (
          <McpNotEnabledBanner canEditAccount={canEditAccount} />
        ) : (
          <Stack gap="300">
            <SelectWorkspace
              clientName={state.clientInfo?.clientName}
              selectedWorkspaces={selectedWorkspaces}
              onSelectWorkspaces={(workspaces) => {
                setSelectedWorkspaces(workspaces);
              }}
            />
            <SelectScopes
              clientName={state.clientInfo?.clientName}
              requestedScopes={requestedScopes}
              selectedScopes={selectedScopes}
              onSelectScopesChange={setSelectedScopes}
            />
          </Stack>
        )}
      </PanelSection>

      <PanelSection withBorder>
        <PrivacyNotice clientInfo={state.clientInfo} />
      </PanelSection>

      <PanelFooter withBorder>
        <PanelActions>
          {!isMcpEnabled ? (
            <Button variant="primary" href={canEditAccount ? '/settings/ai' : '/'}>
              {canEditAccount ? 'Go to AI & MCP Settings' : 'Return to Customer.io'}
            </Button>
          ) : (
            <>
              <Button variant="secondary" href="/">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAllowAccess} isDisabled={isAllowAccessDisabled}>
                Allow access
              </Button>
            </>
          )}
        </PanelActions>
      </PanelFooter>
    </AuthLayout>
  );
}

interface RedirectViewProps {
  clientName?: string;
}

export const RedirectView = ({ clientName }: RedirectViewProps) => (
  <AuthLayout>
    <PanelSection>
      <Stack gap="200" alignBlock="center" alignInline="center">
        <Heading level="3">Taking you back to {clientName ?? 'the app'}…</Heading>
        <Banner variant="information" title="Please wait">
          If the redirect doesn't happen automatically, you can close this window and return to{' '}
          {clientName ?? 'the app'}.
        </Banner>
      </Stack>
    </PanelSection>
  </AuthLayout>
);

interface PrivacyNoticeProps {
  clientInfo?: BaseClientInfo;
}

export const PrivacyNotice = ({ clientInfo }: PrivacyNoticeProps) => {
  const { clientName, clientUri, tosUri, policyUri } = clientInfo ?? {};
  const displayName = isEmpty(clientName) ? 'this app' : clientName;
  const hasPrivacyAndTerms = !isEmpty(tosUri) && !isEmpty(policyUri);
  const hasClientUri = !isEmpty(clientUri);

  return (
    <Card className={trustCard}>
      <CardSection>
        <InlineStack alignBlock="start" gap="100" wrap="nowrap" alignInline="space-between">
          <InlineStack alignBlock="start" gap="100" wrap="nowrap">
            <Box pt="025" flexShrink="0">
              <Icon name="data-privacy" size="sm" color="subtle" />
            </Box>
            <Stack gap="050">
              <Text size="s" weight="medium" color="base">
                Make sure you trust {displayName}
              </Text>
              <Text size="xs" color="subtle">
                Customer.io does not review third-party integrations. Check their{' '}
                {hasPrivacyAndTerms ? (
                  <>
                    <Link href={policyUri} isExternal>
                      Privacy Policy
                    </Link>
                    {' and '}
                    <Link href={tosUri} isExternal>
                      Terms
                    </Link>
                  </>
                ) : (
                  'Privacy Policy and Terms'
                )}{' '}
                before continuing.
              </Text>
            </Stack>
          </InlineStack>
          {hasClientUri ? (
            <Box flexShrink="0">
              <Link href={clientUri} isExternal icon="exit" iconPosition="trailing">
                Website
              </Link>
            </Box>
          ) : null}
        </InlineStack>
      </CardSection>
    </Card>
  );
};

interface McpNotEnabledBannerProps {
  canEditAccount: boolean;
}

const McpNotEnabledBanner = ({ canEditAccount }: McpNotEnabledBannerProps) => (
  <Banner variant="warning" title="MCP not enabled">
    {canEditAccount ? (
      <>
        MCP support is not enabled for your account. To use our MCP server, please go to{' '}
        <Link href="/settings/ai" isUnderlined>
          AI & MCP Settings
        </Link>{' '}
        and enable Customer.io MCP.
      </>
    ) : (
      <>
        MCP support is not enabled for your account. Please ask an account admin to enable it in AI & MCP Settings.{' '}
        <Link href="https://docs.customer.io/ai/mcp-server/#enable-mcp-support-for-your-account">Learn more</Link>
      </>
    )}
  </Banner>
);
